import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  TextField,
  Divider,
  Grid,
} from '@mui/material';
import {
  FiberManualRecord as BulletIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import skillService from '../services/skill.service';
import skillContainerService from '../services/skillContainerService';

interface Container {
  id: number;
  title: string;
  parent: number | null;
  children?: Container[];
  skills?: Skill[];
}

interface Skill {
  id: number;
  name: string;
  description: string;
  container: number | null;
  reference_patterns_details?: any[];
}

interface SkillWithWeight {
  skill_id: number;
  weight: number;
}

interface SelectedSkill {
  skill: Skill;
  weight: number;
}

interface SkillSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (skillsWithWeights: SkillWithWeight[]) => void;
  initialSkills?: SkillWithWeight[];
}

const SkillSelectionModal: React.FC<SkillSelectionModalProps> = ({
  open,
  onClose,
  onSave,
  initialSkills = [],
}) => {
  const [rootContainers, setRootContainers] = useState<Container[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<Map<number, SelectedSkill>>(new Map());

  // Load all skill data in hierarchical structure
  const loadAllData = async () => {
    try {
      setLoading(true);

      // Load root containers (parent = null)
      const rootCont = await skillContainerService.getContainers(null);

      // For each root container, load subcontainers and skills
      const enrichedRoots = await Promise.all(
        rootCont.map(async (root: Container) => {
          // Load subcontainers
          const subcontainers = await skillContainerService.getContainers(root.id);

          // For each subcontainer, load its skills
          const enrichedSubs = await Promise.all(
            subcontainers.map(async (sub: Container) => {
              const skills = await skillService.getAll({ container: sub.id });
              return { ...sub, skills };
            })
          );

          // Load skills directly in root container (without subcontainer)
          const rootSkills = await skillService.getAll({ container: root.id });

          return {
            ...root,
            children: enrichedSubs,
            skills: rootSkills
          };
        })
      );

      setRootContainers(enrichedRoots);
    } catch (error) {
      console.error('Error loading skills data:', error);
      toast.error('Erreur lors du chargement des savoir-faire');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadAllData();
    }
  }, [open]);

  // Initialize selected skills from initial data
  useEffect(() => {
    if (open && rootContainers.length > 0) {
      const newSelectedSkills = new Map<number, SelectedSkill>();

      // Find the skill objects for the initial skill IDs
      initialSkills.forEach((skillData) => {
        const skill = findSkillById(skillData.skill_id);
        if (skill) {
          newSelectedSkills.set(skillData.skill_id, {
            skill,
            weight: skillData.weight || 1.0
          });
        }
      });

      setSelectedSkills(newSelectedSkills);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSkills, rootContainers]);

  // Helper function to find a skill by ID in the hierarchy
  const findSkillById = (skillId: number): Skill | null => {
    for (const root of rootContainers) {
      // Check skills in root
      if (root.skills) {
        const skill = root.skills.find(s => s.id === skillId);
        if (skill) return skill;
      }

      // Check skills in subcontainers
      if (root.children) {
        for (const sub of root.children) {
          if (sub.skills) {
            const skill = sub.skills.find(s => s.id === skillId);
            if (skill) return skill;
          }
        }
      }
    }
    return null;
  };

  const handleToggleSkill = (skill: Skill) => {
    const newSelectedSkills = new Map(selectedSkills);

    if (newSelectedSkills.has(skill.id)) {
      newSelectedSkills.delete(skill.id);
    } else {
      newSelectedSkills.set(skill.id, { skill, weight: 1.0 });
    }

    setSelectedSkills(newSelectedSkills);
  };

  const handleWeightChange = (skillId: number, newWeight: number) => {
    const newSelectedSkills = new Map(selectedSkills);
    const skillData = newSelectedSkills.get(skillId);

    if (skillData) {
      newSelectedSkills.set(skillId, { ...skillData, weight: newWeight });
      setSelectedSkills(newSelectedSkills);
    }
  };

  const handleSave = () => {
    const skillsWithWeights: SkillWithWeight[] = Array.from(selectedSkills.values()).map(
      ({ skill, weight }) => ({
        skill_id: skill.id,
        weight
      })
    );

    onSave(skillsWithWeights);
    onClose();
  };

  const handleCancel = () => {
    setSelectedSkills(new Map());
    onClose();
  };

  const renderSubcontainer = (subcontainer: Container) => (
    <Box key={subcontainer.id} sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        {subcontainer.title}
      </Typography>

      <List dense>
        {subcontainer.skills && subcontainer.skills.length > 0 ? (
          subcontainer.skills.map((skill) => {
            const isSelected = selectedSkills.has(skill.id);
            const selectedData = selectedSkills.get(skill.id);

            return (
              <ListItem
                key={skill.id}
                sx={{
                  py: 0.5,
                  px: 0,
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleToggleSkill(skill)}
                  size="small"
                />
                <BulletIcon sx={{ fontSize: 8, mr: 1 }} />
                <ListItemText
                  primary={skill.name}
                  primaryTypographyProps={{ variant: 'body2' }}
                  sx={{ my: 0, flex: 1 }}
                />
                {isSelected && (
                  <TextField
                    type="number"
                    label="Poids"
                    value={selectedData?.weight || 1.0}
                    onChange={(e) => handleWeightChange(skill.id, parseFloat(e.target.value) || 1.0)}
                    size="small"
                    sx={{ width: 100, ml: 2 }}
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                )}
              </ListItem>
            );
          })
        ) : (
          <ListItem sx={{ py: 0.5, px: 0 }}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Aucun savoir-faire
            </Typography>
          </ListItem>
        )}
      </List>
    </Box>
  );

  const renderRootContainer = (container: Container) => (
    <Paper
      key={container.id}
      sx={{
        p: 3,
        height: '100%',
        border: '2px solid',
        borderColor: 'divider',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {container.title}
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {container.children && container.children.length > 0 ? (
          container.children.map(renderSubcontainer)
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Aucun sous-container
          </Typography>
        )}

        {/* Skills directly in root container */}
        {container.skills && container.skills.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Savoir-faire sans sous-container:
            </Typography>
            <List dense>
              {container.skills.map((skill) => {
                const isSelected = selectedSkills.has(skill.id);
                const selectedData = selectedSkills.get(skill.id);

                return (
                  <ListItem
                    key={skill.id}
                    sx={{
                      py: 0.5,
                      px: 0,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleSkill(skill)}
                      size="small"
                    />
                    <BulletIcon sx={{ fontSize: 8, mr: 1 }} />
                    <ListItemText
                      primary={skill.name}
                      primaryTypographyProps={{ variant: 'body2' }}
                      sx={{ my: 0, flex: 1 }}
                    />
                    {isSelected && (
                      <TextField
                        type="number"
                        label="Poids"
                        value={selectedData?.weight || 1.0}
                        onChange={(e) => handleWeightChange(skill.id, parseFloat(e.target.value) || 1.0)}
                        size="small"
                        sx={{ width: 100, ml: 2 }}
                        inputProps={{ step: 0.1, min: 0 }}
                      />
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </Box>
    </Paper>
  );

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        Sélectionner les savoir-faire
        <Typography variant="body2" color="text.secondary">
          {selectedSkills.size} savoir-faire sélectionné{selectedSkills.size > 1 ? 's' : ''}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {rootContainers.map((container) => (
              <Grid item xs={12} md={6} key={container.id}>
                {renderRootContainer(container)}
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>Annuler</Button>
        <Button variant="contained" onClick={handleSave}>
          Enregistrer ({selectedSkills.size} savoir-faire)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkillSelectionModal;
